import type { OpportunityRoom as SalesforceRoom } from '~/hooks/apis/offers/useGetOffer';

import OffersService from '~/services/OffersService';
import PackagesService from '~/services/PackagesService';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import type { AppliedFlashRoomRate } from '../types';

import {
  buildFlashPackageRoomRateAssignments,
  getMaxExtraNights,
  getOfferPackageName,
  getPackagePersistId,
  getPackageUpdateErrorDetail,
} from './utils';
import type { UnmatchedFlashPackage } from './utils';

export interface MatchPackagesParams {
  aiRoomNameOverrides: Record<string, string>;
  appliedFlashRoomRates: Array<AppliedFlashRoomRate>;
  manualPackageRoomTypeIds: Record<string, string>;
  propertyId: string;
  roomTypes: Array<Pick<App.RoomType, 'id' | 'name'>>;
  salesforceRooms: Array<SalesforceRoom>;
  trimmedSalesforceOfferId: string;
}

export interface MatchPackagesResult {
  assignableRoomTypes: Array<Pick<App.RoomType, 'id' | 'name'>>;
  // True only when the request itself (fetching the offer) failed, as opposed to some
  // packages failing to match -- callers skip the per-package warning snackbar for this.
  isFatalError: boolean;
  matchedPackages: Array<App.Package>;
  matchedRoomRateCount: number;
  unmatchedPackageNames: Array<string>;
  unmatchedPackages: Array<UnmatchedFlashPackage>;
}

export interface LinkPackagesResult extends MatchPackagesResult {
  failures: Array<string>;
  /** Packages refused before any write because flexible nights is on with no max-nights value. */
  flexibleNightsBlockedPackageIds: Array<string>;
  /**
   * Packages this run actually wrote. Linking writes each package independently, so it can
   * half-succeed: callers need these to undo the ones that landed when a sibling failed.
   */
  linkedPackageIds: Array<string>;
  /**
   * Packages whose write was rejected, as opposed to ones that simply didn't match a room by
   * name. The two need telling apart: re-running the match can resolve a name mismatch, but a
   * rejected write needs whatever the backend objected to fixed first (or isn't fixable in the
   * wizard at all).
   */
  updateFailurePackageIds: Array<string>;
}

async function fetchAndMatchPackages({
  aiRoomNameOverrides,
  appliedFlashRoomRates,
  manualPackageRoomTypeIds,
  propertyId,
  roomTypes,
  salesforceRooms,
  trimmedSalesforceOfferId,
}: MatchPackagesParams) {
  const response = await OffersService.getOffer(trimmedSalesforceOfferId);
  const offerPackages = response?.result?.packages ?? [];
  const { assignableRoomTypes, assignments, unmatchedPackageNames, unmatchedPackages } =
    buildFlashPackageRoomRateAssignments({
      aiRoomNameOverrides,
      manualRoomTypeIdByPackageId: manualPackageRoomTypeIds,
      offerPackages,
      propertyId,
      relatedPackageRooms: salesforceRooms,
      roomRates: appliedFlashRoomRates,
      roomTypes,
    });

  return { assignableRoomTypes, assignments, offerPackages, unmatchedPackageNames, unmatchedPackages };
}

function matchedPackagesFromAssignments(offerPackages: Array<App.Package>, assignments: Array<{ packageId: string }>) {
  const matchedPackageIds = new Set(assignments.map((assignment) => assignment.packageId));

  return offerPackages.filter((pkg) => {
    const id = getPackagePersistId(pkg);
    return !!id && matchedPackageIds.has(id);
  });
}

/**
 * Fetches the offer's packages and matches each one to an applied room rate by room name --
 * without writing anything. Used to preview package links against draft room/rate-plan data
 * before any of it is real; the real assignment write happens via `linkPackages` at Submission.
 */
export async function previewPackageLinks(params: MatchPackagesParams): Promise<MatchPackagesResult> {
  try {
    const { assignableRoomTypes, assignments, offerPackages, unmatchedPackageNames, unmatchedPackages } =
      await fetchAndMatchPackages(params);

    return {
      assignableRoomTypes,
      isFatalError: false,
      matchedPackages: matchedPackagesFromAssignments(offerPackages, assignments),
      matchedRoomRateCount: assignments.length,
      unmatchedPackageNames,
      unmatchedPackages,
    };
  } catch (error) {
    reportError(error);

    return {
      assignableRoomTypes: [],
      isFatalError: true,
      matchedPackages: [],
      matchedRoomRateCount: 0,
      unmatchedPackageNames: ['Unable to preview package links.'],
      unmatchedPackages: [],
    };
  }
}

export interface LinkPackagesParams extends MatchPackagesParams {
  flexibleNightsById: Record<string, boolean>;
}

/**
 * Fetches the offer's packages, matches each one to an applied room rate by room name, then
 * applies the resulting fk_property_id/fk_room_type_id/fk_room_rate_id assignments via
 * PackagesService.updatePackage. Callers own applying the result to their own state. Only ever
 * called with real ids (at Submission) -- `previewPackageLinks` is the draft-safe equivalent.
 *
 * The CM's flexible-nights draft rides in the same payload as the link write, since it's the
 * wizard's only write to a package -- applying it separately would either strand it behind an
 * earlier failure or get rejected post-hoc by svc-offer's max-nights validator.
 */
export async function linkPackages(params: LinkPackagesParams): Promise<LinkPackagesResult> {
  try {
    const { assignableRoomTypes, assignments, offerPackages, unmatchedPackages, unmatchedPackageNames } =
      await fetchAndMatchPackages(params);

    // Nothing can un-link a package once written, so a package the backend would reject must be
    // caught before the first write -- re-checked here since Salesforce data may have changed
    // since Package Setup's own check.
    const flexibleNightsBlocked = assignments.filter((assignment) => {
      if (!params.flexibleNightsById[assignment.packageId]) {
        return false;
      }

      const offerPackage = offerPackages.find((pkg) => getPackagePersistId(pkg) === assignment.packageId);
      return !!offerPackage && getMaxExtraNights(offerPackage) === 0;
    });

    // An unmatched package already dooms the submission (its failure triggers a full rollback),
    // so writing the matched ones first would only create links the rollback can't undo.
    if (flexibleNightsBlocked.length > 0 || unmatchedPackageNames.length > 0) {
      return {
        assignableRoomTypes,
        failures: unmatchedPackageNames.concat(
          flexibleNightsBlocked.map((assignment) => {
            const offerPackage = offerPackages.find((pkg) => getPackagePersistId(pkg) === assignment.packageId);
            const label = offerPackage ? getOfferPackageName(offerPackage) : assignment.packageId;
            return `${label}: flexible nights is on but Salesforce has no max extra nights value`;
          }),
        ),
        flexibleNightsBlockedPackageIds: flexibleNightsBlocked.map((assignment) => assignment.packageId),
        isFatalError: false,
        linkedPackageIds: [],
        matchedPackages: matchedPackagesFromAssignments(offerPackages, assignments),
        matchedRoomRateCount: assignments.length,
        unmatchedPackageNames,
        unmatchedPackages,
        updateFailurePackageIds: [],
      };
    }

    const results = await Promise.allSettled(
      assignments.map((assignment) =>
        PackagesService.updatePackage(
          { ...assignment.packageData, flexible_nights: !!params.flexibleNightsById[assignment.packageId] },
          params.trimmedSalesforceOfferId,
          assignment.packageId,
        ),
      ),
    );

    const updateFailureMessages: Array<string> = [];
    const updateFailurePackageIds: Array<string> = [];
    const linkedPackageIds: Array<string> = [];

    results.forEach((result, index) => {
      if (result.status !== 'rejected') {
        linkedPackageIds.push(assignments[index].packageId);
        return;
      }

      reportError(result.reason);
      const matchedPackage = offerPackages.find((pkg) => getPackagePersistId(pkg) === assignments[index].packageId);
      const packageLabel = matchedPackage ? getOfferPackageName(matchedPackage) : assignments[index].packageId;
      const detail = getPackageUpdateErrorDetail(result.reason, getErrorMessage(result.reason));
      updateFailureMessages.push(`${packageLabel}: ${detail}`);
      updateFailurePackageIds.push(assignments[index].packageId);
    });

    // A matched pair can still fail on the update itself, independent of room-name matching.
    const failures = unmatchedPackageNames.concat(updateFailureMessages);

    return {
      assignableRoomTypes,
      failures,
      isFatalError: false,
      // Include packages that failed to save too, so their settings stay editable below.
      matchedPackages: matchedPackagesFromAssignments(offerPackages, assignments),
      flexibleNightsBlockedPackageIds: [],
      linkedPackageIds,
      matchedRoomRateCount: assignments.length,
      unmatchedPackageNames,
      unmatchedPackages,
      updateFailurePackageIds,
    };
  } catch (error) {
    reportError(error);

    return {
      assignableRoomTypes: [],
      failures: ['Unable to link packages to rooms.'],
      flexibleNightsBlockedPackageIds: [],
      isFatalError: true,
      linkedPackageIds: [],
      matchedPackages: [],
      matchedRoomRateCount: 0,
      unmatchedPackageNames: [],
      unmatchedPackages: [],
      updateFailurePackageIds: [],
    };
  }
}
